import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, input, OnChanges, OnDestroy, output, signal, SimpleChanges, ViewChild, QueryList, ViewChildren } from '@angular/core';

export interface TabOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-foxera-tabs-modern',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './foxera-tabs-modern.component.html',
  styleUrls: ['./foxera-tabs-modern.component.css']
})
export class FoxeraTabsModernComponent implements AfterViewInit, OnChanges, OnDestroy {
  readonly tabs = input.required<TabOption[]>();
  readonly activeTab = input.required<string>();
  readonly color = input<'primary' | 'grey'>('primary');
  readonly tabChange = output<string>();

  @ViewChildren('tabBtn') tabButtons!: QueryList<ElementRef<HTMLButtonElement>>;
  @ViewChild('slider') slider!: ElementRef<HTMLDivElement>;

  isViewInitialized = signal(false);
  private resizeObserver?: ResizeObserver;
  private positionUpdateTimeout?: number;

  ngOnChanges(changes: SimpleChanges): void {
    // Update slider position whenever activeTab changes (only after view is initialized)
    if (changes['activeTab'] && this.activeTab() && this.isViewInitialized()) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        this.updateSliderPosition();
      });
    }
  }

  ngAfterViewInit(): void {
    // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.isViewInitialized.set(true);
    
      // Set up ResizeObserver to watch for button size changes
      if (typeof ResizeObserver !== 'undefined' && this.tabButtons?.length > 0) {
        this.resizeObserver = new ResizeObserver(() => {
          // Debounce position updates
          if (this.positionUpdateTimeout) {
            clearTimeout(this.positionUpdateTimeout);
          }
          this.positionUpdateTimeout = window.setTimeout(() => {
            this.updateSliderPosition();
          }, 50);
        });

        this.tabButtons.forEach(btn => {
          if (btn.nativeElement) {
            this.resizeObserver!.observe(btn.nativeElement);
          }
        });
      }

      // Initial positioning with multiple attempts to ensure buttons are rendered
      requestAnimationFrame(() => {
        this.updateSliderPosition();
        // Second attempt after a short delay to ensure layout is stable
        setTimeout(() => {
          this.updateSliderPosition();
          // Third attempt as fallback
          setTimeout(() => {
            this.updateSliderPosition();
            // Final attempt after dialog animation completes (usually ~300ms)
            setTimeout(() => this.updateSliderPosition(), 300);
          }, 100);
        }, 50);
      });
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.positionUpdateTimeout) {
      clearTimeout(this.positionUpdateTimeout);
    }
  }

  onTabClick(value: string): void {
    this.tabChange.emit(value);
    // Immediately update position after click
    if (this.isViewInitialized()) {
      requestAnimationFrame(() => {
        this.updateSliderPosition();
      });
    }
  }

  /**
   * Public method to force position update
   */
  refreshPosition(): void {
    if (this.isViewInitialized()) {
      requestAnimationFrame(() => {
        this.updateSliderPosition();
        setTimeout(() => {
          this.updateSliderPosition();
          setTimeout(() => this.updateSliderPosition(), 100);
        }, 50);
      });
    }
  }

  private updateSliderPosition(): void {
    if (!this.slider?.nativeElement || !this.tabButtons?.length) {
      return;
    }

    const currentTabValue = this.activeTab();
    const tabsArray = this.tabs();
    const activeIndex = tabsArray.findIndex(tab => tab.value === currentTabValue);

    if (activeIndex === -1) {
      return;
    }

    const gap = 8; // gap-2 = 8px
    let leftOffset = 0;
    let activeWidth = 0;

    // Calculate position based on all previous buttons
    this.tabButtons.forEach((btn, index) => {
      const width = btn.nativeElement.offsetWidth;
      
      if (width === 0) {
        return; // Skip if width is not calculated yet
      }

      if (index < activeIndex) {
        leftOffset += width + gap;
      } else if (index === activeIndex) {
        activeWidth = width;
      }
    });

    // Only update if we have valid measurements
    if (activeWidth > 0) {
      this.slider.nativeElement.style.left = `${leftOffset}px`;
      this.slider.nativeElement.style.width = `${activeWidth}px`;
    }
  }
}
